import type { TokenScope, VerifyResponse } from '@dynamic-labs/sdk-api-core';

import { getDefaultClient } from '../../../client/defaultClient';
import { updateAuthFromVerifyResponse } from '../../auth/updateAuthFromVerifyResponse';
import type { OTPVerification } from '../otp.types';
import { verifyOTPForUserSignIn } from './verifyOTPForUserSignIn';
import { verifyOTPForUserUpdate } from './verifyOTPForUserUpdate';

export type VerifyOTPParams = {
  otpVerification: OTPVerification;
  requestedScopes?: TokenScope[];
  verificationToken: string;
};

/**
 * Verifies a one-time password (OTP) for user authentication or updates.
 *
 * This function validates the OTP code provided by the user and completes
 * the verification process for either sign-in or account updates.
 *
 * @param params.otpVerification - The OTP verification object containing verification details.
 * @param params.verificationToken - The token received from the OTP verification process.
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns A promise that resolves to the verify response object upon successful verification.
 */
export const verifyOTP = async (
  { otpVerification, verificationToken, requestedScopes }: VerifyOTPParams,
  client = getDefaultClient()
): Promise<VerifyResponse> => {
  let response: VerifyResponse;
  if (client.user) {
    response = await verifyOTPForUserUpdate(
      {
        otpVerification,
        requestedScopes,
        verificationToken,
      },
      client
    );
  } else {
    response = await verifyOTPForUserSignIn(
      { otpVerification, verificationToken },
      client
    );
  }

  updateAuthFromVerifyResponse({ response }, client);

  return response;
};
